import { motion } from "framer-motion";
import { Reveal } from "./Reveal";

const NODES = [
  { x: 60, y: 60 }, { x: 200, y: 30 }, { x: 340, y: 70 },
  { x: 110, y: 150 }, { x: 250, y: 140 }, { x: 380, y: 180 },
  { x: 50, y: 250 }, { x: 190, y: 240 }, { x: 330, y: 270 },
];

const EDGES = [
  [0, 1], [1, 2], [0, 3], [1, 4], [2, 5], [3, 4], [4, 5],
  [3, 6], [4, 7], [5, 8], [6, 7], [7, 8], [1, 3], [4, 8],
];

const BlockchainVisual = () => (
  <div className="glass-card relative overflow-hidden p-4" data-testid="blockchain-visual">
    <svg viewBox="0 0 430 300" className="w-full">
      {EDGES.map(([a, b], i) => (
        <motion.line
          key={i}
          x1={NODES[a].x} y1={NODES[a].y} x2={NODES[b].x} y2={NODES[b].y}
          stroke="rgba(212,175,55,0.18)"
          strokeWidth="1"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, delay: i * 0.06 }}
        />
      ))}
      {NODES.map((n, i) => (
        <g key={i}>
          <motion.circle
            cx={n.x} cy={n.y} r="14"
            fill="none"
            stroke="rgba(212,175,55,0.35)"
            strokeWidth="1"
            animate={{ scale: [1, 1.6], opacity: [0.6, 0] }}
            transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.3 }}
            style={{ transformOrigin: `${n.x}px ${n.y}px` }}
          />
          <circle cx={n.x} cy={n.y} r="5" fill="#0F0F13" stroke="#D4AF37" strokeWidth="1.5" />
        </g>
      ))}
    </svg>
    <p className="px-3 pb-3 font-mono text-[10px] tracking-[0.25em] text-[#636B78] uppercase">
      Distributed network topology — concept
    </p>
  </div>
);

const BARS = [
  { p: "1.0240", w: 68, side: "bid" }, { p: "1.0235", w: 45, side: "bid" },
  { p: "1.0231", w: 82, side: "bid" }, { p: "1.0228", w: 38, side: "bid" },
  { p: "1.0224", w: 55, side: "ask" }, { p: "1.0220", w: 74, side: "ask" },
  { p: "1.0216", w: 30, side: "ask" }, { p: "1.0212", w: 60, side: "ask" },
];

const ExchangeVisual = () => (
  <div className="glass-card overflow-hidden" data-testid="exchange-visual">
    <div className="flex items-center justify-between border-b border-white/[0.07] px-5 py-4">
      <div className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#D4AF37]/60" />
      </div>
      <span className="font-mono text-[10px] tracking-[0.25em] text-[#636B78] uppercase">WIN Exchange — Preview</span>
    </div>
    <div className="p-5">
      <div className="flex items-end justify-between">
        <div>
          <p className="font-mono text-xs text-[#99A1AF]">$WIN / USDT</p>
          <p className="mt-1 font-display text-3xl font-bold text-white">1.0231</p>
        </div>
        <span className="font-mono text-sm text-emerald-400">+4.62%</span>
      </div>
      <div className="mt-5 space-y-2">
        {BARS.map((b, i) => (
          <div key={i} className="relative flex items-center justify-between rounded-md bg-white/[0.03] px-3 py-2 overflow-hidden">
            <motion.span
              className={`absolute inset-y-0 ${b.side === "bid" ? "right-0 bg-emerald-400/[0.08]" : "right-0 bg-[#D4AF37]/[0.09]"}`}
              initial={{ width: 0 }}
              whileInView={{ width: `${b.w}%` }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: i * 0.05 }}
            />
            <span className={`relative font-mono text-xs ${b.side === "bid" ? "text-emerald-400" : "text-[#F3E5AB]"}`}>{b.p}</span>
            <span className="relative font-mono text-xs text-[#636B78]">{(b.w * 12.4).toFixed(0)} WIN</span>
          </div>
        ))}
      </div>
      <p className="mt-4 font-mono text-[10px] tracking-[0.25em] text-[#636B78] uppercase">
        Interface concept — under development
      </p>
    </div>
  </div>
);

const StatusPill = ({ children, tone }) => (
  <span
    className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 font-mono text-[10px] tracking-[0.25em] uppercase ${
      tone === "amber"
        ? "border-amber-400/30 bg-amber-400/10 text-amber-400"
        : "border-gray-500/30 bg-gray-500/10 text-gray-400"
    }`}
  >
    <span className={`h-1.5 w-1.5 rounded-full ${tone === "amber" ? "bg-amber-400" : "bg-gray-500"}`} />
    {children}
  </span>
);

export default function Infrastructure() {
  return (
    <section className="relative py-24 sm:py-32 border-t border-white/[0.06]" data-testid="infrastructure-section">
      <div className="mx-auto max-w-7xl space-y-28 px-5 sm:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <Reveal>
              <div className="flex items-center gap-3">
                <span className="h-px w-8 bg-[#D4AF37]/60" aria-hidden="true" />
                <span className="font-mono text-xs tracking-[0.3em] text-[#D4AF37] uppercase">Infrastructure</span>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.08]">
                Building the Next Generation of Blockchain Infrastructure.
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-[#99A1AF]">
                Winter Protocol is working toward its own next-generation blockchain infrastructure,
                designed to provide greater transparency, scalability and a stronger technological
                foundation for the future WIN ecosystem.
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <div className="mt-7">
                <StatusPill tone="amber">In Development</StatusPill>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <BlockchainVisual />
          </Reveal>
        </div>

        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal delay={0.15} className="lg:order-2">
            <div>
              <div className="flex items-center gap-3">
                <span className="h-px w-8 bg-[#D4AF37]/60" aria-hidden="true" />
                <span className="font-mono text-xs tracking-[0.3em] text-[#D4AF37] uppercase">Exchange</span>
              </div>
              <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.08]">
                WIN Exchange
              </h2>
              <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-[#99A1AF]">
                A dedicated digital asset exchange is under development as part of the long-term
                Winter Protocol ecosystem vision.
              </p>
              <div className="mt-7">
                <StatusPill tone="amber">Under Development</StatusPill>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:order-1">
            <ExchangeVisual />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
